export interface DeviceDefinition {
    id: string;
    name: string;
    category: 'iPhone' | 'iPad' | 'Display';
    pixels: {
        width: number;
        height: number;
    };
    scale: number;
    points: {
        width: number;
        height: number;
    };
}

export const DEVICE_DEFINITIONS: DeviceDefinition[] = [
    // iPhone devices
    {
        id: 'iphone-16',
        name: 'iPhone 16',
        category: 'iPhone',
        pixels: { width: 2556, height: 1179 },
        scale: 3,
        points: { width: 852, height: 393 },
    },
    {
        id: 'iphone-16-pro',
        name: 'iPhone 16 Pro',
        category: 'iPhone',
        pixels: { width: 2622, height: 1206 },
        scale: 3,
        points: { width: 874, height: 402 },
    },

    // iPad devices
    {
        id: 'ipad-pro-11-std',
        name: 'iPad Pro 11-inch (std)',
        category: 'iPad',
        pixels: { width: 2388, height: 1668 },
        scale: 2,
        points: { width: 1194, height: 834 },
    },
    {
        id: 'ipad-pro-11-m4',
        name: 'iPad Pro 11-inch (M4)',
        category: 'iPad',
        pixels: { width: 2420, height: 1668 },
        scale: 2,
        points: { width: 1210, height: 834 },
    },
    {
        id: 'ipad-pro-13-m4',
        name: 'iPad Pro 13-inch (M4)',
        category: 'iPad',
        pixels: { width: 2752, height: 2064 },
        scale: 2,
        points: { width: 1376, height: 1032 },
    },
    {
        id: 'ipad-pro-12-9',
        name: 'iPad Pro 12.9-inch',
        category: 'iPad',
        pixels: { width: 2732, height: 2048 },
        scale: 2,
        points: { width: 1366, height: 1024 },
    },
    {
        id: 'ipad-air-11',
        name: 'iPad Air 11-inch',
        category: 'iPad',
        pixels: { width: 2360, height: 1640 },
        scale: 2,
        points: { width: 1180, height: 820 },
    },
    {
        id: 'ipad-mini-6-7',
        name: 'iPad Mini 6 & 7',
        category: 'iPad',
        pixels: { width: 2266, height: 1488 },
        scale: 2,
        points: { width: 1133, height: 744 },
    },

    // External displays
    {
        id: 'display-1920x1280',
        name: '1920×1280 Display',
        category: 'Display',
        pixels: { width: 1920, height: 1280 },
        scale: 1.5,
        points: { width: 1280, height: 853 },
    },
];

export type Orientation = 'portrait' | 'landscape';

export interface DeviceConfig {
    device: DeviceDefinition;
    orientation: Orientation;
}

// Default device (iPad Pro 11-inch in portrait) - defined early for use in functions
export const DEFAULT_DEVICE: DeviceDefinition =
    DEVICE_DEFINITIONS.find((d) => d.id === 'ipad-pro-11-std') ||
    DEVICE_DEFINITIONS[2]; // Fallback to iPad Pro 11 by index
export const DEFAULT_ORIENTATION: Orientation = 'portrait';

export function getDeviceDimensions(
    device: DeviceDefinition | undefined,
    orientation: Orientation,
) {
    // Fallback to default device if device is undefined
    const safeDevice = device || DEFAULT_DEVICE;
    const { points } = safeDevice;

    if (orientation === 'landscape') {
        return {
            width: Math.max(points.width, points.height),
            height: Math.min(points.width, points.height),
        };
    } else {
        return {
            width: Math.min(points.width, points.height),
            height: Math.max(points.width, points.height),
        };
    }
}

/**
 * Get device by ID from both built-in and custom devices
 * @param deviceId - The device ID to find
 * @param customDevices - Optional array of custom devices to search
 * @returns The device definition or DEFAULT_DEVICE if not found
 */
export function getDeviceById(
    deviceId: string,
    customDevices: DeviceDefinition[] = [],
): DeviceDefinition {
    // Search custom devices first
    const customDevice = customDevices.find((d) => d.id === deviceId);
    if (customDevice) return customDevice;

    // Fall back to built-in devices
    return DEVICE_DEFINITIONS.find((d) => d.id === deviceId) || DEFAULT_DEVICE;
}

export function getDevicePixelDimensions(
    device: DeviceDefinition | undefined,
    orientation: Orientation,
) {
    // Fallback to default device if device is undefined
    const safeDevice = device || DEFAULT_DEVICE;
    const { pixels } = safeDevice;

    if (orientation === 'landscape') {
        return {
            width: Math.max(pixels.width, pixels.height),
            height: Math.min(pixels.width, pixels.height),
        };
    } else {
        return {
            width: Math.min(pixels.width, pixels.height),
            height: Math.max(pixels.width, pixels.height),
        };
    }
}
